import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { setUserToken } from '../../storage/features/authSlice';
import { getToken, storeToken, storeUserInfo } from '../../storage/LocalStorageService';
import { useLoginUserMutation } from '../../services/userAuthApi';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import GoogleLoginButton from './GoogleLoginButton';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import usePopupMsg from '../../hooks/usePopupMsg';

const UserLogin = ({ onToggleView }) => { 

  const [serverError, setServerError] = useState({});
  const navigate = useNavigate();
  const [loginUser, { isLoading }] = useLoginUserMutation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const {t} = useTranslation("auth");
  const translation = t('login_tran');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const actualData = {
      email: data.get('email'),
      password: data.get('password'),
    };

    const res = await loginUser(actualData);

    if (res.error) {
      setServerError({
        ...res.error.data?.errors,
        non_field_errors: res.error.data?.errors?.non_field_errors || [],
      });
      usePopupMsg.getState().open("fail to login!", "error");
    }

    if (res.data) {
      storeToken(res.data.token);
      storeUserInfo(res.data.user);
      let { access_token } = getToken();
      dispatch(setUserToken({ access_token: access_token }));
      usePopupMsg.getState().open("login successfully.", "success");
      navigate('/');
    }
  };

  let { access_token } = getToken();
  useEffect(() => {
    dispatch(setUserToken({ access_token: access_token }));
  }, [access_token, dispatch]);

  return (
    <>
      <Helmet>
        <title>Login - AddaZakat</title>
        <meta name="description" content="Login to your AddaZakat account to access personalized features and manage your donations." />
        <link rel="canonical" href="https://addazakat.com/auth/login/" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
            {translation.email_label}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="az-input-btn "
            placeholder={translation.email_placeholder}
          />
          {serverError.email && (
            <p className="az-error-message">{serverError.email[0]}</p>
          )}
        </div>

        <div className="relative">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
            {translation.password_label}
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            autoComplete="current-password"
            required
            className="az-input-btn"
            placeholder={translation.password_placeholder}
          />
          <span
            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 dark:text-gray-500 top-7"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <AiFillEyeInvisible className="h-5 w-5" /> : <AiFillEye className="h-5 w-5" />}
          </span>
          {serverError.password && (
            <p className="az-error-message">{serverError.password[0]}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <NavLink
              to="/auth/sendpasswordresetemail"
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-300 dark:hover:text-blue-100"
            >
              {translation.forgot_password}
            </NavLink>
          </div>
        </div>

        <div>
          <button
            type="submit"
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 transition duration-200"
            disabled={isLoading}
          >
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              translation.login_button
            )}
          </button>
        </div>

        {serverError.non_field_errors && (
          <div className="mt-4 text-center text-sm text-red-600 dark:text-red-400">
            {serverError.non_field_errors.map((error, index) => (
              <p key={index} className='az-error-message'>
                {error}{' '}
                {error.includes('User is not verified') && (
                  <NavLink
                    to="/auth/verify-email"
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {translation.verify_email}
                  </NavLink>
                )}
              </p>
            ))}
          </div>
        )}

        <div className="relative flex py-5 items-center">
          <div className="flex-grow border-t border-gray-300 dark:border-gray-100"></div>
          <span className="flex-shrink mx-4 text-gray-500 dark:text-gray-100 text-sm">{translation.or_continue_with}</span>
          <div className="flex-grow border-t border-gray-300 dark:border-gray-100"></div>
        </div>

        <GoogleLoginButton />
      </form>

    </>

  );
};

export default UserLogin;